import { CloseOutlined } from '@ant-design/icons'
import { createColumnHelper } from '@tanstack/react-table'
import { Popconfirm, Tooltip } from 'antd'

import DeliveryTime from '@/components/Atoms/DeliveryTime/DeliveryTime'
import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import PriceUnavailable from '@/components/Feed/PriceUnavailable/PriceUnavailable'
import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import CartAuthorFilter from '@/pages/Cart/CartAuthorFilter/CartAuthorFilter'
import { cartCalculationService } from '@/services/cart/CartCalculationService'
import type {
  CartApiItem,
  CartProductInterface,
  OrganisationInterface,
  StoreShortType,
} from '@/types'
import { formatStockQuantity } from '@/utils/format/formatStockQuantity'
import getProductKey from '@/utils/products/getProductKey'
import { PRICE_UNAVAILABLE_REASON_LABELS } from '@/utils/products/offerAvailability'

import cn from './Cart.module.scss'

export function formatProducts(items: CartApiItem[]): CartProductInterface[] {
  return items.map((item) => ({
    id: item.id,
    addedToTheCartAt: item.addedToTheCartAt,
    availabilityStatus: item.availabilityStatus,
    nomenclatureDto: item.nomenclatureDto,
    authorShortDto: item.authorShortDto,
    prices: {
      sellingPrice: item.sellingPrice ?? null,
      retailPrice: item.retailPrice ?? null,
      priceUpdatedAt: item.priceUpdatedAt,
      priceSource: item.priceSource,
    },
    priceUnavailableReason: item.priceUnavailableReason,
    suppliersStock: [
      {
        supplierId: item.supplierShortDto.id,
        stock: [
          {
            storeShortModel: {
              id: item.storeShortModel.id,
              code: item.storeShortModel.code,
              description: item.storeShortModel.description,
              owner: item.storeShortModel.owner,
              supplierId: item.storeShortModel.supplierId,
              isRemote: item.storeShortModel.isRemote,
              isActive: item.storeShortModel.isActive,
            } as StoreShortType,
            quantity: item.availableQuantity,
            quantityInCart: item.quantity,
            effectiveEtaMinDays: item.effectiveEtaMinDays,
            effectiveEtaMaxDays: item.effectiveEtaMaxDays,
            displayRules: item.displayRules,
            route: item.route,
            stockUpdatedAt: item.stockUpdatedAt,
            cartInfo: {
              id: item.id,
              quantity: item.quantity,
            },
          },
        ],
      },
    ],
  }))
}

function getColumnValue(record: CartProductInterface, prop: string) {
  if (prop === 'retailPrice') return record.prices?.retailPrice
  if (prop === 'sellingPrice') return record.prices?.sellingPrice

  const stockItem = record.suppliersStock?.[0]?.stock?.[0]
  if (!stockItem) return undefined

  switch (prop) {
    case 'storeShortModel':
      return stockItem.storeShortModel
    case 'quantity':
      return stockItem.quantity
    case 'quantityInCart':
      return stockItem.cartInfo?.quantity ?? stockItem.quantityInCart
    case 'etaMinDays':
      return stockItem.effectiveEtaMinDays
    case 'etaMaxDays':
      return stockItem.effectiveEtaMaxDays
  }
}

export function getAvailabilityStatusText(
  product: CartProductInterface
): string | null {
  switch (product.availabilityStatus) {
    case 'AVAILABLE':
      return null
    case 'OUT_OF_STOCK':
      return 'Нет в наличии'
    case 'PARTIALLY_OUT_OF_STOCK': {
      const stockItem = product.suppliersStock?.[0]?.stock?.[0]
      const qty = stockItem?.quantity ?? 0
      const unit =
        product.nomenclatureDto.unitOfMeasurementDto?.description ?? ''
      return `Доступно ${formatStockQuantity(qty, unit, stockItem?.displayRules, '0')}`.trim()
    }
    case 'ROUTE_BROKEN':
      return 'Логистический маршрут недоступен (партнерство разорвано или склад скрыт)'
    case 'SEGMENT_UNAVAILABLE':
      return 'Сегмент цены неактивен. Назначьте клиенту действующий сегмент'
    case 'PRICE_UNAVAILABLE':
      return PRICE_UNAVAILABLE_REASON_LABELS[
        product.priceUnavailableReason ?? 'NO_PRICE'
      ]
    default:
      return null
  }
}

const columnHelper = createColumnHelper<CartProductInterface>()

export const getTanstackColumns = (
  deleteById: (itemId: string) => void,
  duplicateProductQuantities: { [key: string]: number },
  selectedOrganisation: OrganisationInterface,
  organisation: OrganisationInterface,
  updateProducts: (newProducts: CartProductInterface[]) => void,
  authors: { id: string; name: string }[] = []
) => {
  return [
    columnHelper.display({
      id: 'index',
      header: '№',
      cell: (info) =>
        info.row.original.originalIndex !== undefined
          ? info.row.original.originalIndex + 1
          : '-',
    }),

    columnHelper.display({
      id: 'author',
      header: () => (
        <CartAuthorFilter title="Автор и дата добавления" authors={authors} />
      ),
      cell: (info) => {
        const record = info.row.original

        if (!record.addedToTheCartAt) {
          return <span>{record.authorShortDto?.description}</span>
        }

        const date = new Date(record.addedToTheCartAt)
        const formattedDate = date.toLocaleDateString('ru-RU')
        const formattedTime = date.toLocaleTimeString('ru-RU')

        return (
          <span>
            {record.authorShortDto?.description}
            {', '}
            <Tooltip title={formattedTime}>
              <span className={cn.dateHint}>{formattedDate}</span>
            </Tooltip>
          </span>
        )
      },
    }),

    columnHelper.display({
      id: 'article',
      header: 'Артикул',
      cell: (info) => info.row.original.nomenclatureDto.article,
    }),

    columnHelper.display({
      id: 'brand',
      header: 'Бренд',
      cell: (info) => (
        <span>
          {info.row.original.nomenclatureDto.brandDto?.description ?? ''}
        </span>
      ),
    }),

    columnHelper.display({
      id: 'description',
      header: 'Наименование',
      cell: (info) => info.row.original.nomenclatureDto.description,
    }),

    columnHelper.display({
      id: 'store',
      header: 'Склад',
      cell: (info) => {
        const store = getColumnValue(
          info.row.original,
          'storeShortModel'
        ) as StoreShortType
        return <span>{store?.description ?? '-'}</span>
      },
    }),

    columnHelper.display({
      id: 'deliveryTime',
      header: 'Срок доставки',
      cell: (info) => {
        const stockItem = info.row.original.suppliersStock?.[0]?.stock?.[0]
        return (
          <span>
            <DeliveryTime
              minDays={stockItem?.effectiveEtaMinDays}
              maxDays={stockItem?.effectiveEtaMaxDays}
              outOfStock={(stockItem?.quantity ?? 0) <= 0}
            />
          </span>
        )
      },
    }),

    columnHelper.display({
      id: 'quantity',
      header: 'Количество',
      cell: (info) => {
        const record = info.row.original
        const stockItem = record.suppliersStock?.[0]?.stock?.[0]
        const availableQuantity = stockItem?.quantity ?? 0
        const itemDesc =
          record.nomenclatureDto.unitOfMeasurementDto?.description

        const key = String(getProductKey(record))

        const quantityInCart =
          duplicateProductQuantities[key] ??
          stockItem?.cartInfo?.quantity ??
          stockItem?.quantityInCart ??
          0

        return (
          <UpdatedAtTooltip updatedAt={stockItem?.stockUpdatedAt} kind="stock">
            <QuantityToggler
              location="cart"
              element={{
                ...record,
                suppliersStock: [
                  {
                    ...record.suppliersStock?.[0],
                    stock: [
                      {
                        ...stockItem,
                        quantityInCart: quantityInCart,
                        cartInfo: {
                          ...stockItem.cartInfo,
                          quantity: quantityInCart,
                          id: stockItem.cartInfo?.id || '',
                        },
                      },
                    ],
                  },
                ],
              }}
              maxQuantity={availableQuantity}
              itemDesc={itemDesc}
              updateProducts={updateProducts}
              isShowWarning={true}
            />
          </UpdatedAtTooltip>
        )
      },
    }),

    columnHelper.display({
      id: 'segmentPrice',
      header: 'Цена',
      cell: (info) => {
        const record = info.row.original
        if (record.availabilityStatus === 'PRICE_UNAVAILABLE') {
          return (
            <span className={cn.noStockText}>
              <PriceUnavailable
                price={{
                  unavailableReason: record.priceUnavailableReason,
                  priceSource: record.prices.priceSource,
                }}
              />
            </span>
          )
        }

        const priceWithVAT =
          cartCalculationService.getProductPriceWithVAT(record)

        if (priceWithVAT === 'Товар не расценен') {
          return <span className={cn.noStockText}>{priceWithVAT}</span>
        }

        return (
          <UpdatedAtTooltip
            updatedAt={record.prices.priceUpdatedAt}
            kind="price"
            priceSource={record.prices.priceSource}
          >
            {priceWithVAT}
          </UpdatedAtTooltip>
        )
      },
    }),

    columnHelper.display({
      id: 'segmentPriceSum',
      header: 'Сумма',
      cell: (info) => {
        const record = info.row.original
        const stockItem = record.suppliersStock?.[0]?.stock?.[0]
        const quantity =
          stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart ?? 0

        const total = cartCalculationService.calculateProductTotal(
          record,
          quantity
        )

        if (total > 0) {
          return <span>{cartCalculationService.formatPrice(total)}</span>
        }

        const priceWithVAT =
          cartCalculationService.getProductPriceWithVAT(record)
        if (priceWithVAT === 'Товар не расценен') {
          return <span className={cn.noStockText}>Товар не расценен</span>
        }

        return <span>-</span>
      },
    }),

    columnHelper.display({
      id: 'retailPrice',
      header: 'РРЦ',
      cell: (info) => {
        const retailPriceWithVAT =
          cartCalculationService.getProductRetailPriceWithVAT(info.row.original)
        return <span>{retailPriceWithVAT}</span>
      },
    }),

    columnHelper.display({
      id: 'retail',
      header: 'Сумма РРЦ',
      cell: (info) => {
        const record = info.row.original
        const stockItem = record.suppliersStock?.[0]?.stock?.[0]
        const quantity =
          stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart ?? 0

        const total = cartCalculationService.calculateProductRetailTotal(
          record,
          quantity
        )

        if (total > 0) {
          return <span>{cartCalculationService.formatPrice(total)}</span>
        }

        return <span>-</span>
      },
    }),

    columnHelper.display({
      id: 'delete',
      header: '',
      cell: (info) => {
        const record = info.row.original
        const isOrgSelected = Object.keys(selectedOrganisation).length > 0

        return (
          <Popconfirm
            title="Удаление товара"
            description="Хотите удалить товар из корзины?"
            onConfirm={() => {
              const cartItemId =
                record.suppliersStock?.[0]?.stock?.[0]?.cartInfo?.id
              if (cartItemId) {
                deleteById(cartItemId)
              }
            }}
            onCancel={() => null}
            icon={null}
            okText="Да"
            cancelText="Нет"
            overlayClassName={
              isOrgSelected
                ? [cn.popconfirm, cn.organisationSelected].join(' ')
                : cn.popconfirm
            }
            align={{ offset: [-40, -8] }}
          >
            <div
              className={
                isOrgSelected
                  ? [cn.deleteButtonInTable, cn.organisationSelected].join(' ')
                  : cn.deleteButtonInTable
              }
            >
              <CloseOutlined />
            </div>
          </Popconfirm>
        )
      },
    }),
  ]
}
